import React from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { LayoutDashboard, ArrowRightLeft, Receipt, ArrowDownToLine, ArrowUpFromLine, ShieldCheck, User, Globe, ChevronDown, LogOut } from 'lucide-react';
import { StatusPill } from '@/components/fintech/StatusPill';
import { NotificationsBell } from '@/components/fintech/NotificationsBell';
import { api } from '@/lib/api';

const NAV = [
  { to: '/wallet', end: true, icon: LayoutDashboard, key: 'wallet.dashboard' },
  { to: '/wallet/exchange', icon: ArrowRightLeft, key: 'wallet.exchange' },
  { to: '/wallet/transactions', icon: Receipt, key: 'wallet.transactions' },
  { to: '/wallet/deposit', icon: ArrowDownToLine, key: 'wallet.deposit' },
  { to: '/wallet/withdraw', icon: ArrowUpFromLine, key: 'wallet.withdraw' },
  { to: '/wallet/kyc', icon: ShieldCheck, key: 'wallet.kyc' },
  { to: '/wallet/profile', icon: User, key: 'wallet.profile' },
];

export function WalletLayout() {
  const { t, lang, langs, setLang } = useLanguage();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = () => { logout(); nav('/'); };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b bg-white">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/wallet" className="flex items-center gap-2">
            <img src="/logo.png" alt="Concorde Financial Limited" className="h-10 w-auto" />
            <span className="hidden md:inline text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-l border-[hsl(var(--border))] pl-2 ml-1">Client cabinet</span>
          </Link>
          <div className="flex items-center gap-3">
            <StatusPill status={user?.kyc_status || 'NOT_STARTED'} />
            <NotificationsBell apiClient={api} dataTestPrefix="wallet" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="wallet-language-switcher" className="gap-1"><Globe className="h-4 w-4" />{langs.find(l => l.code === lang)?.label}<ChevronDown className="h-3 w-3" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {langs.map(l => <DropdownMenuItem key={l.code} data-testid={`wallet-lang-${l.code}`} onSelect={() => setLang(l.code)}>{l.label} — {l.full}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="wallet-user-menu" className="gap-2">
                  <div className="h-6 w-6 rounded-full bg-[hsl(var(--brand-navy))] text-white flex items-center justify-center text-xs">{(user?.first_name || 'U')[0]}</div>
                  {user?.first_name} {user?.last_name}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/wallet/profile">{t('wallet.profile')}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/business-portal" data-testid="wallet-menu-business-portal">Business portal</Link></DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="wallet-logout"><LogOut className="h-4 w-4 mr-2" />{t('header.logout')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-[1400px] px-3 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="lg:sticky lg:top-24 h-fit">
          <nav className="rounded-2xl border bg-white p-2">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`wallet-nav-${item.to.split('/').pop() || 'dashboard'}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? 'bg-[hsl(var(--brand-navy))] text-white' : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))]'}`}
              >
                <item.icon className="h-4 w-4" />
                {t(item.key)}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
